import { API_BASE_URL } from '../utils/api'

export type CatalogSyncStatus = 'IDLE' | 'PENDING' | 'SYNCING' | 'SYNCED' | 'PARTIAL' | 'ERROR'
export type CatalogSyncEntityType = 'PRODUCT' | 'CATEGORY'

export type CatalogRelationSnapshot = {
  targetId: string
  targetSku?: string | null
  targetSlug?: string | null
  targetName?: string | null
  relationType: string
  priority: number
  vertical: string | null
}

export type CatalogSyncTarget = {
  tenantId: string
  channelInstanceId: string
  channelType: string
  instanceName: string
}

export type CatalogProductSnapshot = {
  id: string
  sku: string
  name: string
  slug: string
  description: string | null
  priceCents: number
  currency: string
  stock: number
  isFeatured: boolean
  imageUrl: string | null
  keywords: string | null
  categoryId: string | null
  categorySlug: string | null
  commercialRole: string | null
  categoryCommercialRole: string | null
  structuredRelationVersion: string
  complementProductRefs: string[]
  complementCategoryRefs: string[]
  complementSourceFields: Array<'keywords' | 'description'>
  firstClassProductRelations: CatalogRelationSnapshot[]
}

export type CatalogCategorySnapshot = {
  id: string
  name: string
  slug: string
  description: string | null
  parentId: string | null
  parentSlug: string | null
  sortOrder: number
  isActive: boolean
  commercialRole: string | null
  updatedAt: string
  firstClassCategoryRelations: CatalogRelationSnapshot[]
}

export type CatalogSnapshotEntry = {
  entityType: CatalogSyncEntityType
  entityId: string
  fingerprint: string
  payload: CatalogProductSnapshot | CatalogCategorySnapshot
}

export type CatalogChangeEntry = CatalogSnapshotEntry & {
  previousPayload?: CatalogProductSnapshot | CatalogCategorySnapshot
  previousFingerprint?: string
}

export type CatalogEntityDiff = {
  added: CatalogSnapshotEntry[]
  updated: CatalogChangeEntry[]
  removed: CatalogChangeEntry[]
  counts: {
    added: number
    updated: number
    removed: number
    pending: number
    current: number
    synced: number
  }
}

export type CatalogSyncStateSummary = {
  id: string
  status: CatalogSyncStatus | string
  lastJobId: string | null
  lastAttemptAt: string | null
  lastSyncedAt: string | null
  lastError: string | null
  productCount: number
  categoryCount: number
  featuredCount: number
  snapshotHash: string | null
  updatedAt: string
}

export type CatalogSyncView = {
  target: CatalogSyncTarget
  state: CatalogSyncStateSummary | null
  diff: {
    products: CatalogEntityDiff
    categories: CatalogEntityDiff
    totalPending: number
  }
  summary: {
    productCount: number
    categoryCount: number
    featuredCount: number
    snapshotHash: string
  }
}

type CatalogSyncViewsResponse = {
  data: CatalogSyncView | CatalogSyncView[]
}

type CatalogSyncRefreshResponse = {
  queued: boolean
  jobId: string | null
}

type CatalogSyncParams = {
  tenantId: string
  channelInstanceId?: string
  sampleLimit?: number
}

type CatalogSyncRefreshParams = {
  tenantId: string
  channelInstanceId?: string
  force?: boolean
}

const getAdminHeaders = (): HeadersInit => {
  const token = localStorage.getItem('monitoring_token')
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: options.method ?? 'GET',
    headers: {
      ...getAdminHeaders(),
      ...(options.headers || {}),
    },
    body: options.body,
  })

  if (!response.ok) {
    const text = await response.text().catch(() => '')
    throw new Error(text || `Catalog sync request failed: ${response.status}`)
  }

  return response.json()
}

function buildQuery(params: Record<string, string | number | undefined>) {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    search.set(key, String(value))
  }
  return search.toString()
}

function normalizeViews(payload: CatalogSyncViewsResponse): CatalogSyncView[] {
  if (Array.isArray(payload.data)) return payload.data
  if (payload.data) return [payload.data]
  return []
}

export const catalogSyncService = {
  async getViews(params: CatalogSyncParams): Promise<CatalogSyncView[]> {
    const query = buildQuery({
      tenantId: params.tenantId,
      channelInstanceId: params.channelInstanceId,
      sampleLimit: params.sampleLimit,
    })

    const payload = await request<CatalogSyncViewsResponse>(`/admin/catalog-sync/status?${query}`)
    return normalizeViews(payload)
  },

  async refresh(params: CatalogSyncRefreshParams): Promise<CatalogSyncRefreshResponse> {
    const query = buildQuery({
      tenantId: params.tenantId,
      channelInstanceId: params.channelInstanceId,
    })

    return request<CatalogSyncRefreshResponse>(`/admin/catalog-sync/refresh?${query}`, {
      method: 'POST',
      body: JSON.stringify({ force: Boolean(params.force) }),
    })
  },
}

export type {
  CatalogSyncViewsResponse,
  CatalogSyncRefreshResponse,
  CatalogSyncParams,
  CatalogSyncRefreshParams,
}
